
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import CommonDialog from "@/components/common/CommonDialog.vue";
import store from "@/store";
import router from "@/router";
import NativeClient, { TitleInfo } from "@/client/native-client";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import CommonUtil, { isValidApiAccessToken } from "@/utils/common-utils";
import { FavoriteHistoryRecipes, FavoriteSendedHistoryRecipes, HTMLDivElementEvent } from "@/types/favorites";
import { CommonRecipe } from "@/types/commonRecipe";
import {
  isEndOfScrollBottom,
  createFavoriteHistoryRecipesToCommonRecipes,
  createFavoriteSendedHistoryRecipesToCommonRecipes
} from "@/utils/favorites-util";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";

export default defineComponent({
  name: "HistoryRecipeListPage",
  components: { CommonRecipeList, CommonDialog },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // urlから選択された種類を取得（デフォルトは送信履歴）
    let kind = 0;
    if (router.currentRoute.value.query.kind != null) {
      kind = Number(router.currentRoute.value.query.kind);
    }

    // タイトル行の設定
    const titleInfo = new TitleInfo(false, "");
    if (kind == 0) {
      titleInfo.title = "送信履歴";
      setLogId(PAGE_ID.FAVORITES_SENDED_LIST);
    } else {
      titleInfo.title = "閲覧履歴";
      setLogId(PAGE_ID.FAVORITES_DISPLAYED_LIST);
    }
    NativeClient.setTitle(titleInfo);
    CommonUtil.scroll({ top: 0, left: 0 });

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      NativeClient.sendDebugLog("MyListFolderPage updateDisplay");
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    //
    // API実行
    //
    let totalCount = 0;
    let recipeCount = 0;
    let isRequesting = false;
    const getRecipeList = async () => {
      NativeClient.sendDebugLog("HistoryRecipeListPage getRecipeList");
      store.dispatch("common/startLoading");
      isRequesting = true;
      await NativeClient.getApiParams();
      if (isValidApiAccessToken()) {
        if (kind == 0) {
          // 送信履歴
          NativeClient.sendDebugLog("HistoryRecipeListPage getRecipeList getSendedHistory");
          await store.dispatch("favorites/getSendedHistory");
          NativeClient.sendDebugLog("HistoryRecipeListPage getRecipeList getSendedHistory end");
          const result: FavoriteSendedHistoryRecipes = store.getters["favorites/getSendedHistory"];
          totalCount = result.totalCount;
          recipeCount = result.recipes.length;
          updateDisplay();
        } else {
          // 閲覧履歴
          NativeClient.sendDebugLog("HistoryRecipeListPage getRecipeList getViewedHistory");
          await store.dispatch("favorites/getViewedHistory");
          NativeClient.sendDebugLog("HistoryRecipeListPage getRecipeList getViewedHistory end");
          const result: FavoriteHistoryRecipes = store.getters["favorites/getViewedHistory"];
          totalCount = result.totalCount;
          recipeCount = result.recipes.length;
          updateDisplay();
        }
      }
      isRequesting = false;
      store.dispatch("common/stopLoading");
    }

    // 実行
    const loadInitialData = async () => {
      NativeClient.sendDebugLog("HistoryRecipeListPage loadInitialData() kind = " + kind);
      if (kind == 0) {
        // 送信履歴
        const result: FavoriteSendedHistoryRecipes = store.getters["favorites/getSendedHistory"];
        if (result == null || result.recipes.length == 0) {
          NativeClient.sendDebugLog("HistoryRecipeListPage loadInitialData() getSendedList");
          getRecipeList();
        } else {
          NativeClient.sendDebugLog("HistoryRecipeListPage loadInitialData() getSendedList");
          totalCount = result.totalCount;
          recipeCount = result.recipes.length;
          updateDisplay();
        }
      } else {
        // 閲覧履歴
        const result: FavoriteHistoryRecipes = store.getters["favorites/getViewedHistory"];
        if (result == null || result.recipes.length == 0) {
          NativeClient.sendDebugLog("HistoryRecipeListPage loadInitialData() getViewedList");
          getRecipeList();
        } else {
          totalCount = result.totalCount;
          recipeCount = result.recipes.length;
          updateDisplay();
        }
      }
    };

    // 更新
    const updatePage = async (info: UpdatePageInfo) => {
      if (info.sendedHistory && kind == 0) {
        NativeClient.sendDebugLog("HistoryRecipeListPage Update sendedHistory");
        await store.dispatch("favorites/updateSendedHistory", null);
        getRecipeList();
      } else if (info.viewedHistory && kind == 1) {
        NativeClient.sendDebugLog("HistoryRecipeListPage Update viewedHistory");
        await store.dispatch("favorites/updateDisplayedHistory", null);
        getRecipeList();
      }
    }

    // ビーコンログ出力
    const sendBeaconLog = () => {
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_DISPLAYED_LIST, null, []);
      if (kind == 0) {
        logInfo.pageId = PAGE_ID.FAVORITES_SENDED_LIST;
      }
      NativeClient.sendViewedLog(logInfo);
    };

    // 本ページの表示／非表示
    let isDisplayed = false;
    onMounted(() => {
      isDisplayed = true;
      loadInitialData();

      // マイリスト更新
      NativeClient.registUpdatePageListener(updatePage);

      sendBeaconLog();
    });
    onUnmounted(() => {
      isDisplayed = false;
    });

    // スクロール検知
    const onScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("HistoryRecipeListPage onScroll totalCount = " + totalCount + ", recipeCount = " + recipeCount);
      if (
        isEndOfScrollBottom(event) &&
        (recipeCount < totalCount) &&
        !isRequesting
      ) {
        NativeClient.sendDebugLog("HistoryRecipeListPage onScroll detect");
        //refRequestType.value = new MyListFolderPageRequestType(5, "", 0);
        getRecipeList();
      }
    };

    // レシピリストを取得
    const recipeList = computed(() => {
      let recipeList: CommonRecipe[] = [];
      if (kind == 0) {
        NativeClient.sendDebugLog("HistoryRecipeListPage recipeList getSendedHistory");
        const sendedList: FavoriteSendedHistoryRecipes = store.getters["favorites/getSendedHistory"];
        if (sendedList != null) {
          recipeList = createFavoriteSendedHistoryRecipesToCommonRecipes(sendedList.recipes)
        } else {
          NativeClient.sendDebugLog("HistoryRecipeListPage recipeList getSendedHistory is Null");
          return null;
        }
      } else {
        NativeClient.sendDebugLog("HistoryRecipeListPage recipeList getViewedHistory");
        const viewedList: FavoriteHistoryRecipes = store.getters["favorites/getViewedHistory"];
        if (viewedList != null) {
          recipeList = createFavoriteHistoryRecipesToCommonRecipes(viewedList.recipes)
        } else {
          NativeClient.sendDebugLog("HistoryRecipeListPage recipeList getViewedHistory is Null");
          return null;
        }
      }
      return recipeList;
    });

    // レシピ選択
    const selectRecipe = (recipeId: number, type: string) => {
      NativeClient.sendDebugLog("HistoryRecipeListPage selectRecipe");
      NativeClient.transitionReceipDetail(recipeId, type);
    };

    // APIリクエスト時のエラーハンドル
    const {
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const showApiErrorDialog = computed(() => {
      const apiError: ApiError = store.getters["common/getApiError"];
      NativeClient.sendDebugLog("HistoryRecipeListPage showApiError");
      if (apiError == null) {
        NativeClient.sendDebugLog("HistoryRecipeListPage showApiError no error");
        return false;
      } else if (!isDisplayed) {
        NativeClient.sendDebugLog("HistoryRecipeListPage showApiError isDisplayed is false");
        return false;
      }
      return true;
    });
    const cancelHandler = () => {
      isRequesting = false;
      closeDialog();
    };
    const retryHandler = () => {
      closeDialog();
      getRecipeList();
    };

    return {
      onScroll,
      recipeList,
      updateDisplayKey,
      selectRecipe,
      showApiErrorDialog,
      getApiErrorTitle,
      getApiErrorMessage,
      cancelHandler,
      retryHandler
    };
  }
});
